import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { customFetcher } from '../useFetch';
import {
  IMeetingRequest,
  IMeetingRequestsResponse,
} from '../../Interfaces/IMeetingRequest';

const QUERY_KEY = 'legaltransactionmeetingrequest';

/** Eine buchbare Option (Terminart + je Terminart nur buchbare Standorte). */
export interface IBookableOption {
  idMeetingType: number;
  Name: string;
  DurationInMin?: number | null;
  locations: { idLocation: number; Name: string | null }[];
  BookingHorizonDays?: number;
}

/**
 * Zentrale buchbare Optionen (Terminarten mit hinterlegten buchbaren Zeiten +
 * je Terminart nur die Standorte mit freien Slots). Grundlage für die
 * gekoppelten Dropdowns und den Hart-Block in der Terminaufforderung.
 */
export const useGetBookingOptions = () => {
  const { data, isLoading: isLoadingBookingOptions } = useQuery<{
    meetingTypes: IBookableOption[];
  }>({
    refetchOnWindowFocus: false,
    queryKey: [QUERY_KEY, 'bookingoptions'],
    queryFn: () => customFetcher({ endpoint: '/booking/options' }),
  });
  return { bookingOptions: data?.meetingTypes ?? [], isLoadingBookingOptions };
};

/** Alle Terminanfragen eines Vorgangs (intern). */
export const useGetLegalTransactionMeetingRequests = (
  idLegalTransaction?: number
) => {
  const {
    data,
    isLoading: isLoadingMeetingRequests,
    refetch: refetchMeetingRequests,
  } = useQuery<IMeetingRequestsResponse>({
    refetchOnWindowFocus: false,
    queryKey: [QUERY_KEY, idLegalTransaction],
    queryFn: () =>
      customFetcher({
        endpoint: `/legaltransaction/${idLegalTransaction}/meetingrequests`,
      }),
    enabled: !!idLegalTransaction && idLegalTransaction > 0,
  });

  const meetingRequests: IMeetingRequest[] = data?.requests ?? [];

  return { meetingRequests, isLoadingMeetingRequests, refetchMeetingRequests };
};

export interface IMeetingRequestMailTemplate {
  Subject: string;
  Body: string;
  DictionaryIds: number[];
}

export type MeetingRequestAction = 'accept' | 'reject' | 'counter';

/** Lädt die (unersetzte) Mandanten-Mailvorlage + Dictionary-IDs für eine Aktion. */
export const useGetMeetingRequestMailTemplate = (
  idMeetingRequest?: number,
  action?: MeetingRequestAction
) => {
  const { data: template, isLoading: isLoadingTemplate } =
    useQuery<IMeetingRequestMailTemplate>({
      refetchOnWindowFocus: false,
      queryKey: [QUERY_KEY, 'mailtemplate', idMeetingRequest, action],
      queryFn: () =>
        customFetcher({
          endpoint: `/meetingrequest/${idMeetingRequest}/mailtemplate?action=${action}`,
        }),
      enabled: !!idMeetingRequest && idMeetingRequest > 0 && !!action,
    });

  return { template, isLoadingTemplate };
};

export interface IFreeBusySlot {
  start: string; // "YYYY-MM-DD HH:MM:SS"
  end: string;
  durationInMin: number;
}

/**
 * Lädt freie Termin-Slots (intern, echtzeit) über das Booking-Free/Busy. Nutzt
 * dieselbe Berechnung wie die öffentliche Terminbuchung, sodass eine hier
 * gewählte Zeit garantiert frei ist (kein 409 beim Gegenvorschlag).
 */
export const useGetMeetingRequestFreeBusy = (
  idMeetingType?: number | null,
  dateFrom?: string,
  dateTo?: string,
  idLocation?: number | null,
  durationInMin?: number | null,
  idRoom?: number | null,
  idUserCalendarConnection?: number | null
) => {
  const params = new URLSearchParams();
  if (idMeetingType) params.set('idMeetingType', String(idMeetingType));
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);
  if (idLocation) params.set('idLocation', String(idLocation));
  if (durationInMin) params.set('durationInMin', String(durationInMin));
  if (idRoom) params.set('idRoom', String(idRoom));
  if (idUserCalendarConnection)
    params.set('idUserCalendarConnection', String(idUserCalendarConnection));

  const { data, isLoading: isLoadingFreeBusy } = useQuery<{
    slots: IFreeBusySlot[];
  }>({
    refetchOnWindowFocus: false,
    queryKey: [
      QUERY_KEY,
      'freebusy',
      idMeetingType,
      dateFrom,
      dateTo,
      idLocation,
      durationInMin,
      idRoom,
      idUserCalendarConnection,
    ],
    queryFn: () =>
      customFetcher({ endpoint: `/booking/freebusy?${params.toString()}` }),
    enabled: !!idMeetingType && !!dateFrom && !!dateTo,
  });

  return { freeBusySlots: data?.slots ?? [], isLoadingFreeBusy };
};

const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => {
  void queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
  void queryClient.invalidateQueries({
    queryKey: ['legaltransaction/fullObject'],
  });
};

/** Terminanfrage annehmen -> erzeugt echten Termin (Backend). */
export const useAcceptMeetingRequest = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: acceptMeetingRequest, isPending: isAccepting } =
    useMutation({
      mutationFn: ({
        idMeetingRequest,
        staffMessage,
        idLocation,
        idRoom,
        subject,
        body,
        coordination,
      }: {
        idMeetingRequest: number;
        staffMessage?: string;
        idLocation?: number;
        idRoom?: number;
        subject?: string;
        body?: string;
        coordination?: {
          mode: 'separate' | 'shared';
          otherClientIds: number[];
          informOthers: boolean;
        };
      }) =>
        customFetcher({
          endpoint: `/meetingrequest/${idMeetingRequest}/accept`,
          method: 'POST',
          body: {
            ...(staffMessage ? { staffMessage } : {}),
            ...(idLocation !== undefined ? { idLocation } : {}),
            ...(idRoom !== undefined ? { idRoom } : {}),
            ...(subject !== undefined ? { Subject: subject } : {}),
            ...(body !== undefined ? { Body: body } : {}),
            ...(coordination ? { coordination } : {}),
          },
        }),
      onSuccess: () => invalidate(queryClient),
    });

  return { acceptMeetingRequest, isAccepting };
};

/** Terminanfrage ablehnen. */
export const useRejectMeetingRequest = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: rejectMeetingRequest, isPending: isRejecting } =
    useMutation({
      mutationFn: ({
        idMeetingRequest,
        staffMessage,
        subject,
        body,
      }: {
        idMeetingRequest: number;
        staffMessage?: string;
        subject?: string;
        body?: string;
      }) =>
        customFetcher({
          endpoint: `/meetingrequest/${idMeetingRequest}/reject`,
          method: 'POST',
          body: {
            ...(staffMessage ? { staffMessage } : {}),
            ...(subject !== undefined ? { Subject: subject } : {}),
            ...(body !== undefined ? { Body: body } : {}),
          },
        }),
      onSuccess: () => invalidate(queryClient),
    });

  return { rejectMeetingRequest, isRejecting };
};

/** Gegenvorschlag zu einer Terminanfrage machen. */
export const useCounterMeetingRequest = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: counterMeetingRequest, isPending: isCountering } =
    useMutation({
      mutationFn: ({
        idMeetingRequest,
        counterProposalStart,
        staffMessage,
        idRoom,
        subject,
        body,
      }: {
        idMeetingRequest: number;
        counterProposalStart: string;
        staffMessage?: string;
        idRoom?: number;
        subject?: string;
        body?: string;
      }) =>
        customFetcher({
          endpoint: `/meetingrequest/${idMeetingRequest}/counter`,
          method: 'POST',
          body: {
            counterProposalStart,
            ...(staffMessage ? { staffMessage } : {}),
            ...(idRoom !== undefined ? { idRoom } : {}),
            ...(subject !== undefined ? { Subject: subject } : {}),
            ...(body !== undefined ? { Body: body } : {}),
          },
        }),
      onSuccess: () => invalidate(queryClient),
    });

  return { counterMeetingRequest, isCountering };
};

/**
 * Setzt das vorgangsbezogene Flag „Mandant soll Termin vereinbaren". Das
 * Backend persistiert es und synchronisiert den Vorgang sofort ins
 * Mandantenportal, wo daraufhin der Handlungsaufruf erscheint/verschwindet.
 */
export const useSetRequestClientMeeting = () => {
  const {
    mutateAsync: setRequestClientMeeting,
    isPending: isSettingRequestClientMeeting,
  } = useMutation({
    mutationFn: ({
      idLegalTransaction,
      value,
    }: {
      idLegalTransaction: number;
      value: boolean;
    }) =>
      customFetcher({
        endpoint: `/legaltransaction/${idLegalTransaction}/requestClientMeeting`,
        method: 'PUT',
        body: { value },
      }),
  });

  return { setRequestClientMeeting, isSettingRequestClientMeeting };
};
